import { useState, useCallback } from 'react';
import useAuth from './useAuth';

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  text: string;
}

const WEBHOOK_BASE_URL = process.env.REACT_APP_CHAT_PROPERTY_LISTING_URL ?? '';

const useChat = () => {
  const { getAuthToken } = useAuth();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  const getHeaders = useCallback((): HeadersInit => {
    const token = getAuthToken() ?? '';
    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      'x-userinfo': token,
    };
  }, [getAuthToken]);

  const initChat = useCallback(
    async (propertyCode: string, marketplaceType: string) => {
      if (isInitialized || !WEBHOOK_BASE_URL || !propertyCode) return;
      setIsLoading(true);
      try {
        const res = await fetch(`${WEBHOOK_BASE_URL}/init`, {
          method: 'POST',
          headers: getHeaders(),
          body: JSON.stringify({ property_code: propertyCode, marketplace_type: marketplaceType }),
        });
        const data = await res.json();
        const replyText = data?.message ?? 'Hola, ¿en qué puedo ayudarte?';
        setMessages([{ id: `b-${Date.now()}`, role: 'assistant', text: String(replyText) }]);
        setIsInitialized(true);
      } catch {
        setMessages([
          { id: `e-${Date.now()}`, role: 'assistant', text: 'Error al inicializar el asistente.' },
        ]);
      } finally {
        setIsLoading(false);
      }
    },
    [isInitialized, getHeaders],
  );

  const sendMessage = useCallback(
    async (text: string) => {
      if (!text.trim() || !WEBHOOK_BASE_URL) return;

      const userMsg: ChatMessage = { id: `u-${Date.now()}`, role: 'user', text: text.trim() };
      setMessages((prev) => [...prev, userMsg]);
      setIsLoading(true);

      try {
        const res = await fetch(`${WEBHOOK_BASE_URL}/chat`, {
          method: 'POST',
          headers: getHeaders(),
          body: JSON.stringify({ message: text.trim() }),
        });
        const data = await res.json();
        const replyText = data?.message ?? 'Sin respuesta';
        setMessages((prev) => [
          ...prev,
          { id: `b-${Date.now()}`, role: 'assistant', text: String(replyText) },
        ]);
      } catch {
        setMessages((prev) => [
          ...prev,
          { id: `e-${Date.now()}`, role: 'assistant', text: 'Error al conectar con el asistente.' },
        ]);
      } finally {
        setIsLoading(false);
      }
    },
    [getHeaders],
  );

  const clearMessages = useCallback(() => {
    setMessages([]);
    setIsInitialized(false);
  }, []);

  return { messages, isLoading, isInitialized, sendMessage, initChat, clearMessages };
};

export default useChat;
